/**
 * Sign-in (ADR-0004): Google Workspace sign-in for G4S users, email +
 * password + authenticator code for invite-only local accounts. There is no
 * sign-up — access is granted by an administrator after training.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Banner, Button, Checkbox, Field, Input } from '../../components';
import { qs } from '../../lib/api/client';
import type { Branding } from '../../lib/api/types';
import { authErrorKey, type MfaChallenge } from '../../lib/auth';
import { iapIdentity, markIapAttempt, shouldTryIapSignIn, usableIapEmail } from '../../lib/auth/iap';
import { useI18n } from '../../lib/i18n';
import { isNative } from '../../lib/device';
import { useQuery } from '../../lib/query';
import { Link } from '../../lib/router';
import { useServices } from '../../app/services';
import { AuthLayout } from './AuthLayout';

/** A per-language setting in the user's language, its base language, else English (B21). Older values may be a plain string. */
export function localizedSetting(v: Record<string, string> | string | null | undefined, lang: string): string | null {
  if (!v) return null;
  if (typeof v === 'string') return v || null;
  return v[lang] || v[lang.split('-')[0]!] || v.en || Object.values(v).find(Boolean) || null;
}

// Users created by the API dev seed (apps/api/src/db/seed-dev.ts).
const DEV_PERSONAS = ['reportador@g4s.com', 'analista@g4s.com', 'gerente.cuenta@g4s.com', 'disenador@g4s.com', 'gerente.pais@g4s.com', 'admin@g4s.com'];

export function LoginPage({ branding, messageKey }: { branding: Branding; messageKey?: string }) {
  const { auth, api } = useServices();
  const { t, lang } = useI18n();
  const [error, setError] = useState<string | null>(messageKey ?? null);
  const [busy, setBusy] = useState<'google' | 'g4sid' | 'password' | 'mfa' | 'reset' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [devMfa, setDevMfa] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [code, setCode] = useState('');
  const [resetSent, setResetSent] = useState(false);
  // The local-account section starts closed on web; on native store builds it is the primary sign-in.
  const native = isNative();
  const [localOpen, setLocalOpen] = useState(false);
  const signInMessage = branding.signInMessage[lang] ?? branding.signInMessage.en;
  const termsUrl = localizedSetting(branding.termsUrl, lang);
  const privacyUrl = localizedSetting(branding.privacyUrl, lang);
  // Without a configured link, the administrator's own terms/privacy texts are shown instead (ADR-0022).
  const legal = useQuery({
    queryKey: ['public', 'legal-texts', lang],
    enabled: !termsUrl || !privacyUrl,
    retry: false,
    staleTime: 10 * 60_000,
    queryFn: () => api.get<{ items: { key: string }[] }>(`/public/legal-texts${qs({ lang })}`, { anonymous: true }),
  });
  const hasText = (key: 'terms' | 'privacy') => !!legal.data?.items.some((i) => i.key === key);

  // Behind Google IAP the person is already authenticated with Google: sign that
  // account in straight away (once per session, never after a deliberate sign-out,
  // never over a problem message such as "not a user") — see lib/auth/iap.ts.
  const [iapEmail, setIapEmail] = useState<string | null>(null);
  useEffect(() => {
    if (native || auth.mode !== 'identity-platform' || !shouldTryIapSignIn({ hasProblemMessage: !!messageKey })) return;
    let cancelled = false;
    void (async () => {
      const domains = (import.meta.env.VITE_WORKSPACE_DOMAIN ?? 'g4s.com').split(',');
      const hint = usableIapEmail(await iapIdentity(), domains);
      if (!hint || cancelled) return;
      markIapAttempt();
      setIapEmail(hint);
      await run('google', () => auth.signInWithGoogle({ loginHint: hint }));
    })();
    return () => {
      cancelled = true;
    };
    // Runs once when the sign-in page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(authErrorKey(e));
    } finally {
      setBusy(null);
    }
  };

  const onPassword = (e: FormEvent) => {
    e.preventDefault();
    void run('password', async () => {
      if (auth.mode === 'dev') {
        await auth.signInDev!(email, devMfa);
        return;
      }
      const r = await auth.signInWithPassword(email.trim(), password);
      if (r.status === 'mfa-required') setChallenge(r.challenge);
    });
  };

  const onMfa = (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    void run('mfa', () => auth.verifyMfa(challenge, code));
  };

  const legalLink = (key: 'terms' | 'privacy', url: string | null) =>
    url ? (
      <a className="underline" href={url} target="_blank" rel="noreferrer">
        {t(`ui.auth.${key}`)}
      </a>
    ) : hasText(key) ? (
      <Link className="underline" to={`/legal/${key}`}>
        {t(`ui.auth.${key}`)}
      </Link>
    ) : null;
  const footer = (
    <span className="inline-flex gap-3">
      {legalLink('terms', termsUrl)}
      {legalLink('privacy', privacyUrl)}
    </span>
  );

  const localForm = (
    <>
      <form onSubmit={onPassword} className="flex flex-col gap-4">
        <Field label={t('ui.auth.email')}>
          {(id) => <Input id={id} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        {auth.mode === 'identity-platform' ? (
          <Field label={t('ui.auth.password')}>
            {(id) => <Input id={id} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}
          </Field>
        ) : (
          <>
            <Banner tone="warning">{t('ui.auth.devMode')}</Banner>
            <Checkbox checked={devMfa} onChange={setDevMfa} label={t('ui.auth.devWithMfa')} />
          </>
        )}
        <Button type="submit" size="lg" block variant={auth.mode === 'identity-platform' && !native ? 'secondary' : 'primary'} loading={busy === 'password'}>
          {t('ui.auth.signIn')}
        </Button>
        {auth.mode === 'identity-platform' &&
          (resetSent ? (
            <p className="text-center text-sm text-success">{t('ui.auth.resetSent')}</p>
          ) : (
            <Button
              variant="ghost"
              disabled={!email}
              loading={busy === 'reset'}
              onClick={() => run('reset', async () => {
                await auth.sendPasswordReset(email.trim());
                setResetSent(true);
              })}
            >
              {t('ui.auth.forgotPassword')}
            </Button>
          ))}
      </form>

      {auth.mode === 'dev' && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {DEV_PERSONAS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setEmail(p)}
              className="rounded-lg border border-border bg-surface-2/80 px-2.5 py-1.5 font-mono text-xs font-medium text-fg transition-colors hover:border-brand/40 hover:bg-brand-soft hover:text-brand"
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </>
  );

  return (
    <AuthLayout branding={branding} footer={footer}>
      {signInMessage && <p className="mb-4 text-center text-muted">{signInMessage}</p>}
      {error && (
        <Banner tone="danger" className="mb-4">
          {t(error)}
        </Banner>
      )}

      {challenge ? (
        <form onSubmit={onMfa} className="flex flex-col gap-4">
          <p className="text-muted">{t('auth.mfaRequired')}</p>
          <Field label={t('ui.auth.code')}>
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
                className="text-center font-mono text-2xl font-semibold tracking-[0.4em]"
              />
            )}
          </Field>
          <Button type="submit" size="lg" block loading={busy === 'mfa'} disabled={code.length < 6}>
            {t('ui.auth.verify')}
          </Button>
          <Button variant="ghost" onClick={() => setChallenge(null)}>
            {t('ui.common.back')}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-5">
          {iapEmail && busy === 'google' && <p className="text-center text-sm text-muted">{t('ui.auth.iapSigningIn', { email: iapEmail })}</p>}
          {auth.mode === 'identity-platform' && (
            // Google Workspace and (when enabled) G4SID (Zitadel OAuth / OIDC) sign-in are available on web, iPhone (iOS), and Google (Android).
            <div className="flex flex-col gap-3">
              <Button size="lg" block loading={busy === 'google'} onClick={() => run('google', () => auth.signInWithGoogle())}>
                <span className="flex size-7 items-center justify-center rounded-md bg-white shadow-sm">
                  <GoogleMark />
                </span>
                {t('ui.auth.google')}
              </Button>
              {branding.zitadel?.enabled && (
                <Button
                  size="lg"
                  block
                  variant="secondary"
                  loading={busy === 'g4sid'}
                  onClick={() => run('g4sid', () => auth.signInWithG4SId())}
                >
                  <img src="/g4sid.png" alt="G4SID" className="h-6 w-auto rounded-md object-contain" />
                  <span>{t('ui.auth.g4sid')}</span>
                </Button>
              )}
            </div>
          )}

          {auth.mode === 'identity-platform' && !native ? (
            // Local accounts (contractors, break-glass admins) are the exception: tucked away.
            <details
              className="group rounded-xl border border-border"
              open={localOpen}
              onToggle={(e) => setLocalOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-4 text-sm font-medium text-muted [&::-webkit-details-marker]:hidden">
                {t('ui.auth.localAccount')}
                <span aria-hidden className="transition-transform group-open:rotate-180">▾</span>
              </summary>
              <div className="border-t border-border p-4">{localForm}</div>
            </details>
          ) : (
            <>
              {auth.mode === 'identity-platform' && (
                <div className="flex items-center gap-3 text-xs font-medium text-muted">
                  <span className="h-px flex-1 bg-border" />
                  <span>{t('ui.auth.orLocal')}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              {localForm}
            </>
          )}

          <p className="text-center text-sm text-muted">{t('ui.auth.noAccount')}</p>
        </div>
      )}
    </AuthLayout>
  );
}

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
